import React, { useEffect, useRef, useState } from "react"
import { View, TouchableOpacity, Platform } from "~/ui/primitives"
import MovToMp4 from "~/ui/noop"
import { observer } from "mobx-react-lite"
import "~/ui/noop"
import { nanoid } from "nanoid"
import { Camera, useCameraDevice } from "~/ui/vision-camera"
import { Video, ResizeMode } from "~/ui/expo-av"
import FastImage from "~/ui/FastImage"
import styled, { useTheme } from '~/ui/styled'
import { useSafeAreaInsets } from "~/ui/safe-area"
import { useSharedValue, withTiming } from "~/ui/reanimated"
import Icon from "~widgets/icon"
import { getAssetInfoFromAssetUri } from "~utils/getAssetDetail"
import useNavigation from "~hooks/useNavigation"
import { useStores } from "../../../models"
import { MessageTypes } from "../../../proto"
import RecordingButton from "./RecordingButton"
// import { globalToast } from "~utils"

const Controls = styled.View<{bottom: number}>`
  position: absolute;
  left: 0;
  bottom: ${({ bottom }) => bottom + 20}px;
  width: 100%;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
`

export const VideoRecorder = observer(() => {
  const {
    chatStore: { current },
    messageStore,
  } = useStores()
  const theme = useTheme()
  const navigation = useNavigation()
  const insets = useSafeAreaInsets()
  const MAX_DURATION = 15000; // 最大录制时长（毫秒）
  const progress = useSharedValue(0); // 定义进度共享值

  const cameraRef = useRef<Camera>(null)
  const [devicePosition, setDevicePosition] = useState<"back" | "front">("back")
  const device = useCameraDevice(devicePosition)
  // console.log(device, 'device')
  const [hasPermission, setHasPermission] = useState(false)
  const [recording, setRecording] = useState(false)
  const [video, setVideo] = useState<any>(null)
  const [photo, setPhoto] = useState<any>(null)

  // 请求相机权限
  useEffect(() => {
    (async () => {
      let cameraPermission = await Camera.getCameraPermissionStatus()
      let micPermission = await Camera.getMicrophonePermissionStatus()

      if (cameraPermission !== 'granted') {
        cameraPermission = await Camera.requestCameraPermission()
      }

      if (micPermission !== 'granted') {
        micPermission = await Camera.requestMicrophonePermission()
      }

      setHasPermission(
        cameraPermission === 'granted' &&
        micPermission === 'granted'
      )
    })()
    return () => {
      try {
        cameraRef.current?.stopRecording?.()
      } catch {}
    }
  }, [])


  async function submitSelectedPhoto({ type, filename, uri, size }) {
    const message = current.newMessage({
      content: "",
      attach: { imgs: [{ type, filename, uri, size }] },
      type: MessageTypes.IMAGES,
      reply_msg_id: null,
    })
    navigation.pop(1)
    messageStore.sendMessageByMessageType(message)
  }

  async function submitSelectedVideo({ type, filename, uri, size }) {
    if (Platform.OS === "ios") {
      const result = await MovToMp4.convertMovToMp4(uri, filename)
      uri = !result.startsWith("file://") ? "file://" + result : result
    }
    const message = current.newMessage({
      content: "",
      attach_path: uri,
      attach: { type, filename, uri, size },
      type: MessageTypes.VIDEO,
      reply_msg_id: null,
    })
    navigation.pop(1)
    messageStore.sendMessageByMessageType(message)
  }
   
  if (!device || !hasPermission) return null
 
  return (
    <View style={{ flex: 1 }}>
      {!video && !photo && (
        <View style={{ flex: 1 }}>
          <Camera
            ref={cameraRef}
            style={{ flex: 1 }}
            device={device}
            isActive={true}
            photo={true}
            video={true}
            audio={true}
            preview={true}
            enableZoomGesture={true}
            photoQualityBalance="balanced"
          />
          <Controls bottom={insets.bottom}>
            {!recording && (
              <View style={{ width: 40 }}></View>
            )}
            
            {/* 录制按钮 */}
            <RecordingButton
              onTakePhoto={async () => {
                // 单击：拍照
                try {
                  // 检查当前设备是否有闪光灯，没有则传 'off'
                  const flashMode = device?.hasFlash ? 'auto' : 'off';
                  const photoFile = await cameraRef.current?.takePhoto({
                    flash: flashMode,
                  });
                  const assetInfo = await getAssetInfoFromAssetUri(photoFile.path);
                  setPhoto({
                    type: "image/jpg",
                    filename: `IMAGE-${nanoid()}-${Date.now()}.jpg`,
                    uri: assetInfo.uri,
                    size: assetInfo.size ?? 0,
                  });
                } catch (e) {
                  // console.error("takePhoto error", e);
                }
              }}
              onStartRecord={async () => {
                if (recording) return;

                // 启动动画（让 progress 从 0 → 1）
                progress.value = 0;
                progress.value = withTiming(1, { duration: MAX_DURATION });

                setRecording(true);

                try {
                  await cameraRef.current?.startRecording({
                    flash: "off",
                    onRecordingFinished: async (videoFile) => {
                      const assetInfo = await getAssetInfoFromAssetUri(videoFile.path);
                      setVideo({
                        type: "video/mp4",
                        filename: `VIDEO-${nanoid()}-${Date.now()}.mp4`,
                        uri: assetInfo.uri,
                        size: assetInfo.size ?? 0,
                      });
                      setRecording(false);
                      progress.value = 0;
                    },
                    onRecordingError: (error) => {
                      // console.error("Recording error", error);
                      setRecording(false);
                      progress.value = 0;
                    },
                  });
                } catch (e) {
                  // console.error("startRecording error", e);
                  setRecording(false);
                  progress.value = 0;
                }
              }}
              onStopRecord={async () => {
                // 松手：停止录制
                if (!recording) return;

                progress.value = 0; // 重置动画

                try {
                  await cameraRef.current?.stopRecording();
                } catch (e) {
                  // console.error("stopRecording error", e);
                }
              }}
            >
            </RecordingButton>

            {!recording && (
              <TouchableOpacity
                onClick={() =>
                  setDevicePosition((p) => (p === "back" ? "front" : "back"))
                }
              >
                <Icon name={"pz"} size={40} color={theme.colors.background} />
              </TouchableOpacity>
            )}

          </Controls>
        </View>
       
      )}

      {/* 视频预览 */}
      {video && (
        <>
          <Video
            style={{ flex: 1 }}
            useNativeControls={true}
            src={video.uri}
            resizeMode={ResizeMode.COVER}
            shouldPlay
            isLooping
          />
          <View
            style={{
              position: "absolute",
              left: 0,
              bottom: insets.top + 20,
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              paddingHorizontal: 20,
            }}
          >
            <TouchableOpacity onClick={() => setVideo(undefined)}>
              <View
                style={{
                  height: 50,
                  width: 50,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Icon name={"close"} size={20} color={theme.colors.background} />
              </View>
            </TouchableOpacity>
            <TouchableOpacity onClick={() => submitSelectedVideo(video)}>
              <View
                style={{
                  height: 50,
                  width: 50,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Icon name={"fs"} size={50} color={theme.colors.primary} />
              </View>
            </TouchableOpacity>
          </View>
        </>
      )}

      {/* 图片预览 */}
      {photo && (
        <>
          <FastImage
            style={{ flex: 1 }}
            src={photo.uri}
            resizeMode="cover"
          />
          <View
            style={{
              position: "absolute",
              left: 0,
              bottom: insets.top + 20,
              flexDirection: "row",
              justifyContent: "space-between",
              width: "100%",
              paddingHorizontal: 20,
            }}
          >
            <TouchableOpacity onClick={() => setPhoto(undefined)}>
              <View
                style={{
                  height: 50,
                  width: 50,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Icon name={"close"} size={20} color={theme.colors.background} />
              </View>
            </TouchableOpacity>
            <TouchableOpacity onClick={() => submitSelectedPhoto(photo)}>
              <View
                style={{
                  height: 50,
                  width: 50,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Icon name={"fs"} size={50} color={theme.colors.primary} />
              </View>
            </TouchableOpacity>
          </View>
        </>
      )}
    </View>
  )
})

export default VideoRecorder
